import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { LoyaltySettingsType, ReputationWithoutTargetType, WalletWithReputationDtoType } from '@sifca-monorepo/widget-generator';

type ReputationMilestoneState = 'current' | 'reached' | 'upcoming';

interface ReputationMilestoneViewModel {
  id: string;
  title: string;
  pointsLabel: string;
  color: string;
  iconUrl: string | null;
  state: ReputationMilestoneState;
  isLocked: boolean;
}

interface ReputationHeroViewModel {
  accentColor: string;
  accentGlow: string;
  currentTitle: string;
  currentIconUrl: string | null;
  pointsIconUrl: string;
  nextLevelTitle: string;
  currentPointsText: string;
  goalPointsText: string;
  remainingPointsText: string;
  progress: number;
  isFinalLevel: boolean;
  milestones: ReputationMilestoneViewModel[];
}

@Component({
  standalone: true,
  selector: 'reputation-hero',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './reputation-hero.component.html',
  styleUrls: ['./reputation-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileReputationHeroComponent implements OnChanges {
  private readonly elementRef = inject(ElementRef);
  private readonly translateService = inject(TranslateService);

  @Input() disabled = false;
  @Input() darkMode = false;
  @Input() isFinalLevel = false;
  @Input() reputationPicture = '';
  @Input() currentLevelPercentage = 0;
  @Input() wallet: WalletWithReputationDtoType | null = null;
  @Input() loyaltySettings: LoyaltySettingsType | null = null;
  @Input() nextLevel: ReputationWithoutTargetType | null = null;
  @Input() currentLevel: ReputationWithoutTargetType | null = null;

  @Output() cardClick = new EventEmitter<void>();

  hero: ReputationHeroViewModel | null = null;
  cardInfoStyle: Record<string, string>;

  ngOnChanges(): void {
    this.hero = this.buildReputationHeroViewModel();
  }

  private updateCardTheme(): void {
    const bg = (this.currentLevel?.color || this.loyaltySettings?.prelevel?.color || '').toString().trim();
    const isDark = this.isDarkColor(bg);
    const text = isDark ? '#ffffff' : '#0f172a';
    const subtext = isDark ? 'rgba(255,255,255,0.86)' : 'rgba(15,23,42,0.72)';

    const hostEl: HTMLElement = this.elementRef.nativeElement;
    hostEl.style.setProperty('--card-text', text);
    hostEl.style.setProperty('--card-subtext', subtext);

    // Keep cardInfoStyle in sync if still needed elsewhere
    this.cardInfoStyle = {
      'background': bg,
      '--card-text': text,
      '--card-subtext': subtext,
    };
  }

  private isDarkColor(input: string): boolean {
    const rgb = this.parseColorToRgb(input);
    if (!rgb) {
      return true;
    }

    const toLinear = (value: number) => {
      const channel = value / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    };

    const r = toLinear(rgb.r);
    const g = toLinear(rgb.g);
    const b = toLinear(rgb.b);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 0.4;
  }

  onCardClick(): void {
    if (this.disabled || !this.hero) {
      return;
    }

    this.cardClick.emit();
  }

  onCardSpace(event: Event): void {
    event.preventDefault();
    this.onCardClick();
  }

  trackReputationMilestone(index: number, milestone: ReputationMilestoneViewModel): string {
    return milestone?.id || `milestone-${index}`;
  }

  private buildReputationHeroViewModel(): ReputationHeroViewModel | null {
    if (!this.wallet?.reputationLevels?.length) {
      return null;
    }

    const sortedLevels = [...this.wallet.reputationLevels].sort((left, right) => (left?.rank ?? 0) - (right?.rank ?? 0));
    const activeLevel = this.currentLevel ?? null;
    const nextLevel = this.nextLevel ?? sortedLevels[0] ?? null;
    const accentColor = this.getHeroAccentColor(activeLevel, nextLevel);
    const currentPoints = Math.max(0, Math.round(+(this.wallet?.amount ?? 0)));
    const goalPoints = this.isFinalLevel ? currentPoints : Math.max(currentPoints, +(nextLevel?.levelInterval?.max ?? currentPoints));
    const remainingPoints = this.getRemainingPointsToNextLevel(nextLevel);
    this.updateCardTheme();
    return {
      accentColor,
      accentGlow: this.toRgba(accentColor, 0.28),
      currentTitle: activeLevel ? this.getReputationLevelTitle(activeLevel) : this.getPreLevelTitle(),
      currentIconUrl: activeLevel ? this.getReputationLevelPicture(activeLevel) : this.getPreLevelPicture(),
      pointsIconUrl: this.getReputationPointsIconUrl(),
      nextLevelTitle: this.getNextLevelTitle(nextLevel, activeLevel),
      currentPointsText: this.formatCompactPoints(currentPoints),
      goalPointsText: this.formatCompactPoints(goalPoints),
      remainingPointsText: this.formatCompactPoints(remainingPoints),
      progress: this.clampProgress(this.currentLevelPercentage ?? 0),
      isFinalLevel: !!this.isFinalLevel,
      milestones: this.buildReputationMilestones(sortedLevels, activeLevel),
    };
  }

  private buildReputationMilestones(
    levels: ReputationWithoutTargetType[],
    activeLevel: ReputationWithoutTargetType | null,
  ): ReputationMilestoneViewModel[] {
    const milestones: ReputationMilestoneViewModel[] = [];

    if (this.loyaltySettings?.prelevel) {
      milestones.push({
        id: 'prelevel',
        title: this.getPreLevelTitle(),
        pointsLabel: this.formatCompactPoints(0),
        color: (this.loyaltySettings.prelevel.color || '#94a3b8').toString(),
        iconUrl: this.getPreLevelPicture(),
        state: activeLevel ? 'reached' : 'current',
        isLocked: false,
      });
    }

    levels.forEach((level, index) => {
      const isCurrent = !!activeLevel && this.isSameLevel(activeLevel, level);
      const isReached = !isCurrent && this.isUnlockedReputationLevel(level, activeLevel);

      milestones.push({
        id: level?.id || `level-${index}`,
        title: this.getReputationLevelTitle(level),
        pointsLabel: this.formatCompactPoints(+(level?.levelInterval?.max ?? 0)),
        color: (level?.color || '#94a3b8').toString(),
        iconUrl: this.getReputationLevelPicture(level),
        state: isCurrent ? 'current' : isReached ? 'reached' : 'upcoming',
        isLocked: !isCurrent && !isReached,
      });
    });

    return milestones;
  }

  private isSameLevel(left: ReputationWithoutTargetType | null, right: ReputationWithoutTargetType | null): boolean {
    if (!left || !right) {
      return false;
    }

    if (left?.id && right?.id) {
      return left.id === right.id;
    }

    return left?.reputationLevel === right?.reputationLevel;
  }

  private isUnlockedReputationLevel(level: ReputationWithoutTargetType | null, activeLevel: ReputationWithoutTargetType | null): boolean {
    if (!level || !activeLevel) {
      return false;
    }

    const currentRank = activeLevel?.rank ?? 0;
    const levelRank = level?.rank ?? 0;

    if (currentRank && levelRank) {
      return currentRank > levelRank;
    }

    const currentMax = +(activeLevel?.levelInterval?.max ?? 0);
    const levelMax = +(level?.levelInterval?.max ?? 0);
    return currentMax > levelMax;
  }

  private getRemainingPointsToNextLevel(nextLevel: ReputationWithoutTargetType | null): number {
    if (!this.wallet || this.isFinalLevel || !nextLevel?.levelInterval?.max) {
      return 0;
    }

    return Math.max(0, Math.round((nextLevel.levelInterval.max ?? 0) - +this.wallet.amount));
  }

  private getHeroAccentColor(activeLevel: ReputationWithoutTargetType | null, nextLevel: ReputationWithoutTargetType | null): string {
    return (activeLevel?.color || this.loyaltySettings?.prelevel?.color || nextLevel?.color || '#1d4ed8').toString();
  }

  private getNextLevelTitle(nextLevel: ReputationWithoutTargetType | null, activeLevel: ReputationWithoutTargetType | null): string {
    if (this.isFinalLevel) {
      return activeLevel ? this.getReputationLevelTitle(activeLevel) : this.getPreLevelTitle();
    }

    return nextLevel ? this.getReputationLevelTitle(nextLevel) : this.getPreLevelTitle();
  }

  private getReputationLevelTitle(level?: ReputationWithoutTargetType | null): string {
    if (!level) {
      return '';
    }

    const locale = this.getLocaleCode();
    const baseLocale = locale.split('-')[0];
    const translation =
      level.translation?.find((entry: any) => entry?.language?.code?.toLowerCase() === locale) ||
      level.translation?.find((entry: any) => entry?.language?.code?.toLowerCase() === baseLocale) ||
      level.translation?.find((entry: any) => entry?.language?.code?.toLowerCase()?.startsWith(`${baseLocale}-`));

    return translation?.content?.reputationLevel || level.reputationLevel || '';
  }

  private getPreLevelTitle(): string {
    const locale = this.getLocaleCode();
    const baseLocale = locale.split('-')[0];
    const translation =
      this.loyaltySettings?.prelevel?.translation?.find((entry: any) => entry?.language?.code?.toLowerCase() === locale) ||
      this.loyaltySettings?.prelevel?.translation?.find((entry: any) => entry?.language?.code?.toLowerCase() === baseLocale) ||
      this.loyaltySettings?.prelevel?.translation?.find((entry: any) => entry?.language?.code?.toLowerCase()?.startsWith(`${baseLocale}-`));

    return translation?.content?.reputationLevel || this.loyaltySettings?.prelevel?.name || 'Prelevel';
  }

  private getLocaleCode(): string {
    return (this.translateService.currentLang || localStorage.getItem('elvklang') || 'en-gb').toLowerCase();
  }

  private getReputationLevelPicture(level?: ReputationWithoutTargetType | null): string | null {
    return this.buildPictureUrl(level?.picture) || this.reputationPicture;
  }

  private getPreLevelPicture(): string | null {
    return this.buildPictureUrl(this.loyaltySettings?.prelevel?.picture) || this.reputationPicture;
  }

  private getReputationPointsIconUrl(): string {
    return this.buildPictureUrl(this.loyaltySettings?.qualitative?.picture) || this.reputationPicture;
  }

  private buildPictureUrl(picture?: { baseUrl?: string | null; path?: string | null } | null): string | null {
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }

    return null;
  }

  private formatCompactPoints(value: number): string {
    const formatted = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(value || 0)));
    return formatted.replace(/\u00A0|\s|,/g, '.');
  }

  private clampProgress(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.max(0, Math.min(100, value));
  }

  private toRgba(input: string, alpha: number): string {
    const rgb = this.parseColorToRgb(input);

    if (!rgb) {
      return `rgba(29, 78, 216, ${alpha})`;
    }

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  }

  private parseColorToRgb(input: string): { r: number; g: number; b: number } | null {
    if (!input) {
      return null;
    }

    const value = input.trim();
    const hex = value.startsWith('#') ? value.slice(1) : '';

    if (hex) {
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16),
        };
      }

      if (hex.length === 6 || hex.length === 8) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
        };
      }
    }

    const rgbMatch = value.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i);
    if (!rgbMatch) {
      return null;
    }

    return {
      r: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[1])))),
      g: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[2])))),
      b: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[3])))),
    };
  }
}
